import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { useMembershipRedux } from '@hooks'
import { PrivacyPolicyModal } from '@components'
import { ROUTES, COLORS, customToast } from '@common'
import {
    extractErrorMessage,
    isEmailExistsError,
    checkPasswordStrength,
    getPasswordStrengthClass,
    encryptPassword,
    emailLoginSchema,
    emailRegistrationSchema,
} from '@utils'
// import { handleTokenError } from '@utils/tokenErrorHandler'
import { LOGIN_ERROR_CODES } from '@utils/errorCodes'
import { ErrorTrackingAPI } from '@services'

const MembershipAuthPage = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const {
        setEmailVerificationData,
        setError,
        clearError,
        isLoading,
        error,
        registerMember,
        getMemberAccessToken,
        requireOTP,
        getCurrentMember,
    } = useMembershipRedux()
    const [isLogin, setIsLogin] = useState(true)
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false)
    const [passwordStrength, setPasswordStrength] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
    })

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
    } = useForm({
        resolver: yupResolver(isLogin ? emailLoginSchema : emailRegistrationSchema),
        defaultValues: {
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
            isRememberMe: false,
            agreeToPrivacyPolicy: false,
        },
    })

    const password = watch('password')
    const email = watch('email')

    // Check password strength for registration
    useEffect(() => {
        if (!isLogin && password) {
            setPasswordStrength(checkPasswordStrength(password))
        } else {
            setPasswordStrength({
                length: false,
                uppercase: false,
                lowercase: false,
                number: false,
            })
        }
    }, [password, isLogin])

    // Check if redirected from login with unverified email
    useEffect(() => {
        if (location.state?.email && location.state?.isResendVerification) {
            setEmailVerificationData({
                email: location.state.email,
                isRegistration: false,
            })
            navigate(ROUTES.Membership.emailVerification, { replace: true })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, setEmailVerificationData])

    useEffect(() => {
        setIsLogin(true)
        clearError()
        reset({
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
            isRememberMe: false,
            agreeToPrivacyPolicy: false,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onSubmit = async data => {
        clearError()

        try {
            if (isLogin) {
                // Login flow
                const result = await getMemberAccessToken({
                    email: data.email,
                    password: data.password,
                    isRememberMe: data.isRememberMe || false,
                })

                if (result.type.endsWith('/fulfilled')) {
                    // Get member data using the access token
                    await getCurrentMember()

                    customToast.success('Login successful!', { duration: 2000 })
                    navigate(ROUTES.root)
                } else {
                    // Handle error based on errorCode
                    const errorPayload = result.payload
                    let errorMessage = 'Login failed. Please try again.'
                    let errorCodeValue = null

                    // Extract error and errorCode from payload
                    if (errorPayload && typeof errorPayload === 'object') {
                        errorMessage = errorPayload.error || errorMessage
                        errorCodeValue = errorPayload.errorCode
                    } else if (typeof errorPayload === 'string') {
                        errorMessage = errorPayload
                    }

                    // Handle based on errorCode
                    if (errorCodeValue === LOGIN_ERROR_CODES.EMAIL_NOT_VERIFIED) {
                        // Email not verified: navigate to verification page
                        customToast.warning('Please verify your email first', { duration: 3000 })
                        setEmailVerificationData({
                            email: data.email,
                            isRegistration: false,
                        })
                        navigate(ROUTES.Membership.emailVerification, {
                            state: { shouldAutoRequestOTP: true },
                        })
                    } else if (
                        errorCodeValue === LOGIN_ERROR_CODES.ACCOUNT_SUSPENDED ||
                        errorCodeValue === LOGIN_ERROR_CODES.ACCOUNT_ARCHIVED
                    ) {
                        // Account status issues
                        customToast.warning(errorMessage || 'Your account has been suspended. Please contact support.', {
                            duration: 5000,
                        })
                        setError(errorMessage)
                    } else if (errorCodeValue === LOGIN_ERROR_CODES.PASSWORD_NOT_SET) {
                        // Password not set: navigate to password reset
                        customToast.warning('Password not set. Please reset your password.', { duration: 3000 })
                        navigate(ROUTES.Membership.requestPasswordReset)
                    } else {
                        // Other errors (INVALID_CREDENTIALS, etc.)
                        setError('Invalid email or password.')
                    }
                }
            } else {
                // Registration flow
                const result = await registerMember({
                    name: data.name,
                    email: data.email,
                    password: data.password,
                })

                if (result.type.endsWith('/fulfilled')) {
                    // Registration successful, OTP will be automatically sent to email
                    const encryptedPassword = await encryptPassword(data.password)
                    setEmailVerificationData({
                        email: data.email,
                        password: encryptedPassword,
                        isRegistration: true,
                    })
                    customToast.success('Verification code sent to your email', { duration: 3000 })

                    // Auto navigate to email verification page
                    navigate(ROUTES.Membership.emailVerification)
                } else {
                    // Handle registration error
                    const errorMessage = result.payload || 'Registration failed. Please try again.'

                    // Check if email already exists
                    if (isEmailExistsError(errorMessage)) {
                        setError(errorMessage)
                        // Switch to login mode and pre-fill email after showing error
                        // Using setTimeout to allow user to read the error message first
                        setTimeout(() => {
                            setIsLogin(true)
                            reset({
                                email: data.email,
                                password: '',
                                isRememberMe: false,
                            })
                        }, 2000)
                    } else {
                        setError(errorMessage)
                    }
                }
            }
        } catch (error) {
            ErrorTrackingAPI.sendError(error, 'MembershipAuthPage', 'handleSubmit')
            setError('An unexpected error occurred. Please try again.')
        }
    }

    const handleResendVerification = async () => {
        if (!email) {
            setError('Please enter your email address first.')
            return
        }

        clearError()
        const result = await requireOTP({ email })

        if (result.type.endsWith('/fulfilled')) {
            const payload = result.payload || {}
            setEmailVerificationData({
                email,
                isRegistration: false,
                nextAvailableTime: payload.nextAvailableTime || null,
            })
            customToast.success('Verification code sent to your email', { duration: 3000 })
            navigate(ROUTES.Membership.emailVerification)
        } else {
            // Handle error
            const errorMessage = extractErrorMessage(result.payload, 'Failed to send verification code. Please try again.')
            setError(errorMessage)
        }
    }

    return (
        <div className="membership-auth-page">
            <div className="auth-container">
                <div className="auth-header">
                    <div className="auth-icon">{/* <i className={`bx ${isLogin ? 'bx-log-in' : 'bx-user-plus'}`}></i> */}</div>
                    <h2 onClick={() => navigate(-1)}>{isLogin ? 'Member Sign In' : 'Member Sign Up'}</h2>
                    {isLogin ?
                        <p>
                            Sign in to receive <b>2%</b> credit rebate for every order
                        </p>
                    :   <p>Join our membership program</p>}
                </div>
                {!isLogin && (
                    <div
                        className="signup-benefits"
                        style={{
                            marginTop: '1.5rem',
                            padding: '16px',
                            backgroundColor: COLORS.SUCCESS.background,
                            borderRadius: '8px',
                            border: `1px solid ${COLORS.SUCCESS.border}`,
                            fontSize: '14px',
                            lineHeight: '1.6',
                            color: COLORS.SUCCESS.text,
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <div>
                                <strong style={{ display: 'block', marginBottom: '4px', fontSize: '15px' }}>
                                    Free Membership Benefits
                                </strong>
                                <span>
                                    Sign up our free membership to get a credit rebate of <strong>2%</strong> for every order!
                                    Plus, unlock the <strong>advance takeaway function</strong> like a VIP!
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="alert alert-danger mt-3">
                        <i className="bx bx-error-circle"></i>
                        {error}
                        {isEmailExistsError(error) && (
                            <div style={{ marginTop: '0.5rem' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsLogin(true)
                                        reset({
                                            email: watch('email'),
                                            password: '',
                                            isRememberMe: false,
                                        })
                                        clearError()
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: COLORS.INFO.text,
                                        fontWeight: '500',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        padding: 0,
                                    }}
                                >
                                    Click here to sign in instead
                                </button>
                            </div>
                        )}
                    </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} style={{ marginTop: '2rem' }}>
                    {!isLogin && (
                        <div className="form-group">
                            <label htmlFor="name">
                                <i className="bx bx-user"></i> Full Name
                            </label>
                            <input
                                id="name"
                                type="text"
                                {...register('name')}
                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                placeholder="Enter your full name"
                                autoComplete="name"
                                disabled={isLoading}
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="email">
                            <i className="bx bx-envelope"></i> Email
                        </label>
                        <input
                            id="email"
                            type="email"
                            {...register('email')}
                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                            placeholder="Enter your email address"
                            autoComplete="email"
                            disabled={isLoading}
                        />
                        {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">
                            <i className="bx bx-lock"></i> Password
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                {...register('password')}
                                className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                placeholder="Enter your password"
                                autoComplete={isLogin ? 'current-password' : 'new-password'}
                                disabled={isLoading}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#666',
                                    fontSize: '1.2rem',
                                }}
                            >
                                <i className={`bx ${showPassword ? 'bx-show' : 'bx-hide'}`}></i>
                            </button>
                        </div>
                        {errors.password && <div className="invalid-feedback">{errors.password.message}</div>}
                        {!isLogin && password && (
                            <div className="password-strength" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                                {
                                    Object.values(passwordStrength).every(Boolean) ?
                                        // All requirements met - show single success message
                                        <div
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.25rem',
                                                color: COLORS.SUCCESS.text,
                                            }}
                                        >
                                            <i
                                                className="bx bx-check-circle"
                                                style={{
                                                    color: COLORS.SUCCESS.text,
                                                    fontSize: '1rem',
                                                }}
                                            ></i>
                                            <span>All password requirements met</span>
                                        </div>
                                        // Show detailed requirements list
                                    :   <div className={`strength-indicator ${getPasswordStrengthClass(passwordStrength)}`}>
                                            <span
                                                className={passwordStrength.length ? 'valid' : 'invalid'}
                                                style={{
                                                    marginRight: '0.5rem',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                }}
                                            >
                                                <i
                                                    className={`bx ${passwordStrength.length ? 'bx-check-circle' : 'bx-x-circle'}`}
                                                    style={{
                                                        color: passwordStrength.length ? COLORS.SUCCESS.text : COLORS.ERROR.text,
                                                        fontSize: '1rem',
                                                    }}
                                                ></i>
                                                At least 8 characters
                                            </span>
                                            <span
                                                className={passwordStrength.uppercase ? 'valid' : 'invalid'}
                                                style={{
                                                    marginRight: '0.5rem',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                }}
                                            >
                                                <i
                                                    className={`bx ${passwordStrength.uppercase ? 'bx-check-circle' : 'bx-x-circle'}`}
                                                    style={{
                                                        color:
                                                            passwordStrength.uppercase ? COLORS.SUCCESS.text : COLORS.ERROR.text,
                                                        fontSize: '1rem',
                                                    }}
                                                ></i>
                                                One uppercase letter
                                            </span>
                                            <span
                                                className={passwordStrength.lowercase ? 'valid' : 'invalid'}
                                                style={{
                                                    marginRight: '0.5rem',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                }}
                                            >
                                                <i
                                                    className={`bx ${passwordStrength.lowercase ? 'bx-check-circle' : 'bx-x-circle'}`}
                                                    style={{
                                                        color:
                                                            passwordStrength.lowercase ? COLORS.SUCCESS.text : COLORS.ERROR.text,
                                                        fontSize: '1rem',
                                                    }}
                                                ></i>
                                                One lowercase letter
                                            </span>
                                            <span
                                                className={passwordStrength.number ? 'valid' : 'invalid'}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                }}
                                            >
                                                <i
                                                    className={`bx ${passwordStrength.number ? 'bx-check-circle' : 'bx-x-circle'}`}
                                                    style={{
                                                        color: passwordStrength.number ? COLORS.SUCCESS.text : COLORS.ERROR.text,
                                                        fontSize: '1rem',
                                                    }}
                                                ></i>
                                                One number
                                            </span>
                                        </div>

                                }
                            </div>
                        )}
                    </div>

                    {!isLogin && (
                        <div className="form-group">
                            <label htmlFor="confirmPassword">
                                <i className="bx bx-lock"></i> Confirm Password
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    {...register('confirmPassword')}
                                    className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                                    placeholder="Confirm your password"
                                    autoComplete="new-password"
                                    disabled={isLoading}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: '#666',
                                        fontSize: '1.2rem',
                                    }}
                                >
                                    <i className={`bx ${showConfirmPassword ? 'bx-show' : 'bx-hide'}`}></i>
                                </button>
                            </div>
                            {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword.message}</div>}
                        </div>
                    )}

                    {!isLogin && (
                        <div className="form-group remember-me-group">
                            <div className="checkbox-wrapper">
                                <input
                                    id="agreeToPrivacyPolicy"
                                    type="checkbox"
                                    {...register('agreeToPrivacyPolicy')}
                                    className="form-check-input"
                                    disabled={isLoading}
                                />
                                <label htmlFor="agreeToPrivacyPolicy" className="form-check-label">
                                    I agree to the{' '}
                                    <button
                                        type="button"
                                        onClick={() => setIsPrivacyModalOpen(true)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: COLORS.INFO.text,
                                            fontWeight: '500',
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                            padding: 0,
                                        }}
                                    >
                                        Privacy Policy
                                    </button>
                                </label>
                            </div>
                            {errors.agreeToPrivacyPolicy && (
                                <div className="invalid-feedback" style={{ marginTop: '0.25rem' }}>
                                    {errors.agreeToPrivacyPolicy.message}
                                </div>
                            )}
                        </div>
                    )}

                    {isLogin && (
                        <div className="form-group remember-me-group">
                            <div className="checkbox-wrapper">
                                <input
                                    id="isRememberMe"
                                    type="checkbox"
                                    {...register('isRememberMe')}
                                    className="form-check-input"
                                    disabled={isLoading}
                                />
                                <label htmlFor="isRememberMe" className="form-check-label">
                                    Remember me for 30 days
                                </label>
                            </div>
                        </div>
                    )}

                    <button type="submit" className="btn-submit" disabled={isLoading}>
                        {isLoading ?
                            <>
                                <i className="bx bx-loader-alt bx-spin"></i> Processing...
                            </>
                        :   <>{isLogin ? 'Sign In' : 'Sign Up'}</>}
                    </button>
                </form>

                {isLogin && (
                    <div
                        style={{
                            marginTop: '1rem',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem',
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => navigate(ROUTES.Membership.requestPasswordReset)}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: COLORS.INFO.text,
                                fontWeight: '500',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                            }}
                        >
                            Forgot Password?
                        </button>
                        <button
                            type="button"
                            onClick={handleResendVerification}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: COLORS.INFO.text,
                                fontWeight: '500',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                            }}
                        >
                            Resend verification code
                        </button>
                    </div>
                )}

                <div className="auth-divider">
                    <span>Or</span>
                </div>

                <div className="auth-switch">
                    <button
                        type="button"
                        onClick={() => {
                            setIsLogin(!isLogin)
                            clearError()
                            reset({
                                name: '',
                                email: watch('email') || '',
                                password: '',
                                confirmPassword: '',
                                isRememberMe: false,
                                agreeToPrivacyPolicy: false,
                            })
                        }}
                    >
                        {isLogin ? "Don't have an account?" : 'Already have an account? Sign In'}
                        {isLogin && <p className="m-0">Sign up for our free membership today</p>}
                    </button>
                </div>
            </div>

            <PrivacyPolicyModal isOpen={isPrivacyModalOpen} toggle={() => setIsPrivacyModalOpen(false)} />
        </div>
    )
}

export default MembershipAuthPage
